import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import SalonService from "../../services/SalonService";
import { toast } from "react-toastify";
import "./AdvancePaymentWeb.css";
import "../../style.css";
import {
    FaQrcode, FaCopy, FaCheckCircle,
    FaMobileAlt, FaShieldAlt, FaInfoCircle
} from "react-icons/fa";

export default function AdvancePaymentWeb() {
    const navigate = useNavigate();
    const location = useLocation();
    const { customerId, salonUuid, bookingRef, amount } = location.state || {};
    const [upiUrl, setUpiUrl] = useState("");
    const [utr, setUtr] = useState("");
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (salonUuid && bookingRef && amount) {
            loadUpi();
        }
    }, [salonUuid, bookingRef, amount]);

    const upiId = useMemo(() => {
        if (!upiUrl) return "";
        const match = upiUrl.match(/pa=([^&]+)/);
        return match ? decodeURIComponent(match[1]) : "";
    }, [upiUrl]);

    const maskedUpiId = useMemo(() => {
        if (!upiId || !upiId.includes("@")) return upiId;
        const [name, bank] = upiId.split("@");
        if (name.length <= 4) return `****@${bank}`;
        return `${name.slice(0, 2)}****@${bank}`;
    }, [upiId]);

    const loadUpi = async () => {
        try {
            setLoading(true);
            const res = await SalonService.getUpiPaymentLink({
                customerId,
                salonUuid,
                bookingRef,
                amount,
                note: `Advance for booking ${bookingRef}`,
            });
            setUpiUrl(res?.data?.upiUrl || "");
        } catch {
            toast.error("Unable to generate UPI payment details");
        } finally {
            setLoading(false);
        }
    };

    const copyUpiDetails = async () => {
        if (!upiId) return;
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(upiId);
            } else {
                const textarea = document.createElement("textarea");
                textarea.value = upiId;
                textarea.style.position = "fixed";
                textarea.style.opacity = "0";
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);
            }
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Copy not supported on this device");
        }
    };

    const submitUtr = async () => {
        if (!utr || utr.trim().length < 8) {
            toast.error("Please enter a valid UTR / Transaction ID");
            return;
        }

        try {
            setLoading(true);
            await SalonService.updateUpiPayment({
                salonUuid,
                bookingRef,
                utr: utr.trim(),
                amount,
            });
            toast.success("Payment submitted for verification");
            navigate("/home");
        } finally {
            setLoading(false);
        }
    };

    if (!salonUuid || !bookingRef || !amount) {
        return (
            <div className="payment-error-container">
                <div className="error-card shadow">
                    <FaInfoCircle className="error-icon" />
                    <h3>Invalid payment link</h3>
                    <p>Please open the payment page from your appointment list.</p>
                    <button className="btn btn-primary rounded-pill w-100 py-2" onClick={() => navigate("/home")}>
                        Go to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="payment-page-wrapper">
            <div className="payment-content-container py-4">
                <div className="premium-payment-card shadow-lg">
                    <div className="amount-highlight-box">
                        <span className="payment-label">TOTAL PAYABLE</span>
                        <div className="amount-display">
                            <span className="currency-symbol">₹</span>
                            <span className="amount-number">{amount}</span>
                        </div>
                        <div className="ref-tag">Ref: {bookingRef}</div>
                    </div>

                    {upiUrl ? (
                        <div className="upi-method-section">
                            <div className="section-divider-premium">
                                <span>PAY VIA UPI</span>
                            </div>

                            <div className="qr-container-premium">
                                <div className="qr-frame">
                                    <QRCodeSVG value={upiUrl} size={180} level="H" includeMargin={true} />
                                </div>
                                <p className="qr-hint-text">
                                    <FaQrcode className="me-1" /> Scan with any UPI App
                                </p>
                            </div>

                            <div className="upi-id-action-row">
                                <div className="upi-id-premium-box" onClick={copyUpiDetails}>
                                    <div className="upi-id-info">
                                        <span className="upi-label">UPI ID</span>
                                        <span className="upi-val">{maskedUpiId}</span>
                                    </div>
                                    <div className={`copy-btn-premium ${copied ? 'copied' : ''}`}>
                                        {copied ? <FaCheckCircle /> : <FaCopy />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="loading-upi-placeholder">
                            <div className="spinner-border text-primary" role="status"></div>
                            <p className="mt-2">Generating payment link...</p>
                        </div>
                    )}

                    <div className="verification-step-section">
                        <div className="section-divider-premium">
                            <span>VERIFY PAYMENT</span>
                        </div>
                        <p className="step-hint">Enter the 12-digit UTR/Transaction ID after payment</p>

                        <div className="utr-input-wrapper">
                            <FaMobileAlt className="input-icon-premium" />
                            <input
                                className="premium-utr-input"
                                placeholder="UTR / Transaction ID"
                                value={utr}
                                onChange={(e) => setUtr(e.target.value)}
                            />
                        </div>

                        <button
                            onClick={submitUtr}
                            className="premium-submit-btn"
                            disabled={loading || !upiUrl}
                        >
                            {loading ? "Verifying..." : "Confirm & Submit Payment"}
                        </button>
                    </div>

                    <div className="payment-security-footer">
                        <div className="security-tag">
                            <FaShieldAlt className="me-1" /> Secure Verification
                        </div>
                        <p className="security-note">
                            Verification usually takes 5-10 minutes during salon hours.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
